/**
 * WorkspaceManageSheet.js
 *
 * Premium bottom sheet to manage ONE workspace: rename, duplicate, move left /
 * right, delete, and (on the first workspace) go back to the classic dashboard.
 * Clones the PremiumSheet etalon (solid pane, orange primary pill).
 *
 * @module components/workspaces/WorkspaceManageSheet
 */

import React, {useEffect, useState} from 'react';
import {View} from 'react-native';
import {useTranslation} from 'react-i18next';
import PremiumSheet, {SheetInput, SheetLabel} from '../management/PremiumSheet';
import {SheetRow} from './WorkspaceUI';

/**
 * @param {object} props
 * @param {boolean} props.visible Whether the sheet is shown.
 * @param {?{id: string, name: string}} props.board Workspace being managed.
 * @param {boolean} props.canDelete False for the last workspace.
 * @param {boolean} props.canDuplicate False once the workspace cap is reached.
 * @param {boolean} props.canMoveLeft Whether a left neighbour exists.
 * @param {boolean} props.canMoveRight Whether a right neighbour exists.
 * @param {boolean} props.canDisable Offer "back to the classic dashboard".
 * @param {() => void} props.onClose Dismiss.
 * @param {(name: string) => void} props.onRename Save a new name.
 * @param {() => void} props.onDuplicate Duplicate.
 * @param {(direction: number) => void} props.onMove Move -1 / +1.
 * @param {() => void} props.onDelete Delete (the caller confirms).
 * @param {() => void} props.onDisable Switch workspaces off.
 * @returns {JSX.Element} The sheet.
 */
export default function WorkspaceManageSheet({
    visible, board, canDelete, canDuplicate, canMoveLeft, canMoveRight, canDisable,
    onClose, onRename, onDuplicate, onMove, onDelete, onDisable,
}) {
    const {t} = useTranslation();
    const [name, setName] = useState('');

    useEffect(() => {
        if (visible && board) {
            setName(board.name);
        }
    }, [visible, board]);

    const dirty = !!board && name.trim() !== '' && name.trim() !== board.name;

    return (
        <PremiumSheet
            visible={visible}
            onClose={onClose}
            title={t('wsd_manage_title')}
            subtitle={board?.name}
            icon="tab"
            confirmLabel={t('wsd_save')}
            confirmIcon="check"
            confirmDisabled={!dirty}
            onConfirm={() => onRename(name)}
        >
            <SheetLabel>{t('wsd_workspace_name')}</SheetLabel>
            <SheetInput
                value={name}
                onChangeText={setName}
                placeholder={t('wsd_workspace_name_placeholder')}
                maxLength={40}
                returnKeyType="done"
                onSubmitEditing={() => dirty && onRename(name)}
            />
            <View style={{height: 14}} />
            <SheetRow icon="content-copy" title={t('wsd_duplicate')} onPress={onDuplicate} disabled={!canDuplicate} />
            <SheetRow icon="arrow-left" title={t('wsd_move_left')} tone="info" onPress={() => onMove(-1)} disabled={!canMoveLeft} />
            <SheetRow icon="arrow-right" title={t('wsd_move_right')} tone="info" onPress={() => onMove(1)} disabled={!canMoveRight} />
            {canDisable && (
                <SheetRow icon="view-dashboard-outline" title={t('wsd_disable')} subtitle={t('wsd_disable_desc')} tone="neutral" onPress={onDisable} />
            )}
            <SheetRow icon="trash-can-outline" title={t('wsd_delete')} tone="danger" onPress={onDelete} disabled={!canDelete} />
        </PremiumSheet>
    );
}
